import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { addMonths, format, parseISO } from "date-fns"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fetchBookings } from "~/apis/bookings"
import { SOURCE_BADGES, cleanerFirstName, isBlock, isLive, propertyColor, shortName } from "~/lib/calendar"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"

const CLEAN_CHIP = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  done: "bg-muted text-muted-foreground",
}

function CleanChip({ clean }) {
  const base = "rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"

  if (!clean || clean.status === "declined") {
    return <span className={cn(base, "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300")}>no clean yet</span>
  }
  if (clean.flagged_reason) {
    return <span className={cn(base, "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300")} title={clean.flagged_reason}>⚠ clean flagged</span>
  }

  return (
    <span className={cn(base, CLEAN_CHIP[clean.status] || CLEAN_CHIP.done)}>
      🧹 {cleanerFirstName(clean)} · {format(parseISO(clean.window_starts_on), "d MMM")} · {clean.status}
    </span>
  )
}

// Upcoming stays as a flat list, grouped by check-in month. Fetches its own
// 12-month window so it isn't tied to whichever month the grid is showing.
export default function BookingList({ properties }) {
  const [ bookings, setBookings ] = useState(null)
  const [ error, setError ] = useState(null)
  const [ propertyFilter, setPropertyFilter ] = useState("all")

  useEffect(() => {
    const today = new Date()
    fetchBookings(format(today, "yyyy-MM-dd"), format(addMonths(today, 12), "yyyy-MM-dd"))
      .then(setBookings)
      .catch((err) => setError(err.message))
  }, [])

  if (error) return <p className="text-destructive">{error}</p>
  if (bookings === null) return <PageSpinner />

  const propertyById = Object.fromEntries(properties.map((p) => [ p.id, p ]))
  const rows = bookings.filter(
    (b) =>
      isLive(b) && !isBlock(b) &&
      b.property_id in propertyById &&
      (propertyFilter === "all" || String(b.property_id) === propertyFilter),
  )

  const groups = rows.reduce((acc, b) => {
    const key = format(parseISO(b.starts_on), "MMMM yyyy")
    ;(acc[key] = acc[key] || []).push(b)
    return acc
  }, {})

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Select value={propertyFilter} onValueChange={setPropertyFilter}>
          <SelectTrigger size="sm" aria-label="Filter by property">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{properties.length === 2 ? "Both properties" : "All properties"}</SelectItem>
            {properties.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>{shortName(p)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {rows.length} upcoming booking{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {rows.length === 0 && (
        <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          No upcoming bookings{propertyFilter !== "all" ? " for this property" : ""}.
        </p>
      )}

      {Object.entries(groups).map(([ month, monthRows ]) => (
        <section key={month} className="mb-5">
          <h2 className="mb-2 text-sm font-semibold tracking-wide text-muted-foreground uppercase">{month}</h2>
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {monthRows.map((b) => (
              <Link key={b.id} to={routes.bookingDetail(b.id)} className="flex items-center gap-3 p-3 hover:bg-muted/50">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: propertyColor(propertyById[b.property_id]) }}
                  title={b.property_name}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {b.guest_label}
                    {b.guest_count ? <span className="font-normal text-muted-foreground"> ×{b.guest_count}</span> : null}
                    {b.needs_enrichment && <span className="text-xs font-medium text-amber-600 dark:text-amber-400"> · needs details</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(b.starts_on), "EEE d MMM")} → {format(parseISO(b.ends_on), "EEE d MMM")}
                    <span className="text-muted-foreground/70"> · {b.nights} night{b.nights === 1 ? "" : "s"}</span>
                  </p>
                </div>
                <div className="hidden shrink-0 sm:block">
                  <CleanChip clean={b.clean} />
                </div>
                <span className={cn("shrink-0 rounded-full px-2 py-1 text-xs font-semibold", SOURCE_BADGES[b.source] || SOURCE_BADGES.direct)}>
                  {b.source}
                </span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
